import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress,
  Chip,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import Barcode from 'react-barcode';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Add as AddIcon2,
  Remove as RemoveIcon,
  ExpandMore as ExpandMoreIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  DeleteOutline as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { getItems, createItem, updateItem, deleteItem, Item, listItemProperties, createItemProperty, updateItemProperty, deleteItemProperty, ItemProperty, getItemImages, ItemImage, makeItemLabel, listTastingNotes, createTastingNote, updateTastingNote, deleteTastingNote, TastingNote } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../hooks/useResponsive';

const Items: React.FC = () => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [formData, setFormData] = useState<Partial<Item>>({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState<{
    created: number;
    updated: number;
    inventoryUpdated: number;
    errors: string[];
  } | null>(null);
  // Removed unused inventory state - now using direct API calls
  const [propertiesExpanded, setPropertiesExpanded] = useState(false);
  const [properties, setProperties] = useState<Array<Partial<ItemProperty>>>([]);
  const [originalProperties, setOriginalProperties] = useState<Array<Partial<ItemProperty>>>([]);
  const [currentItemImages, setCurrentItemImages] = useState<ItemImage[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  
  // Tasting notes state
  const [tastingNotesExpanded, setTastingNotesExpanded] = useState(false);
  const [tastingNotes, setTastingNotes] = useState<TastingNote[]>([]);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [editingNoteSource, setEditingNoteSource] = useState('');
  const [editingNoteFname, setEditingNoteFname] = useState('');
  const [editingNoteLname, setEditingNoteLname] = useState('');
  const [editingNoteEmail, setEditingNoteEmail] = useState('');
  const [newNoteText, setNewNoteText] = useState('');
  


  useEffect(() => {
    const t = setTimeout(() => fetchItems(), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await getItems(1, 100, search);
      const itemsData = response.data.data || [];
      
      setItems(itemsData);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to load items';
      setError(errorMessage);
      console.error('Items error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setEditingItem(null);
    setFormData({});
    setCurrentItemImages([]);
    setOpenDialog(true);
  };

  const handleEditItem = (item: Item) => {
    setEditingItem(item);
    setFormData(item);
    // Load properties, images, and tasting notes for this item
    (async () => {
      try {
        // Load properties
        const res = await listItemProperties(item.id!);
        const props: ItemProperty[] = res.data.data || [];
        setProperties(props);
        setOriginalProperties(props);
        
        // Load images
        const imagesRes = await getItemImages(item.id!);
        const images: ItemImage[] = imagesRes.data.data || [];
        setCurrentItemImages(images);
        
        // Load tasting notes
        const notesRes = await listTastingNotes(item.id!);
        const notes = notesRes.data.data || [];
        setTastingNotes(notes);
      } catch {
        setProperties([]);
        setOriginalProperties([]);
        setCurrentItemImages([]);
        setTastingNotes([]);
      }
    })();
    setOpenDialog(true);
  };

  const handleDeleteItem = async (code: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        // Find item by code since we might not have id
        const item = items.find(i => i.code === code);
        if (item && item.id) {
          await deleteItem(item.id);
          fetchItems();
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to delete item';
        setError(errorMessage);
      }
    }
  };

  const handleAlcoholToggle = async (item: Item) => {
    try {
      await updateItem(item.id!, { is_alcohol: !item.is_alcohol });
      fetchItems();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update alcohol status';
      setError(errorMessage);
    }
  };

  const handleInventoryUpdate = async (item: any, change: number) => {
    try {
      const newOnHand = Math.max(0, (item.on_hand || 0) + change);
      await updateItem(item.id!, { on_hand: newOnHand });
      fetchItems();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update inventory';
      setError(errorMessage);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingItem) {
        await updateItem(editingItem.id!, formData);
        // Sync properties
        const itemId = editingItem.id!;
        const originalById = new Map<number, ItemProperty>();
        originalProperties.forEach(p => { if (p.id) originalById.set(p.id, p as ItemProperty); });
        const currentIds = new Set<number>();
        for (const p of properties) {
          const name = (p.name || '').trim();
          const value = (p.value || '').trim();
          if (!p.id) {
            if (name && value) {
              await createItemProperty({ item_id: itemId, name, value });
            }
          } else {
            currentIds.add(p.id);
            const orig = originalById.get(p.id);
            if (orig && (orig.name !== name || orig.value !== value)) {
              await updateItemProperty(p.id, { name, value });
            }
          }
        }
        for (const orig of originalProperties) {
          if (orig.id && !currentIds.has(orig.id)) {
            await deleteItemProperty(orig.id);
          }
        }
      } else {
        // Create item then add properties
        const createRes = await createItem(formData as Item);
        const newItemId = createRes.data?.data?.id;
        if (newItemId) {
          for (const p of properties) {
            const name = (p.name || '').trim();
            const value = (p.value || '').trim();
            if (name && value) {
              await createItemProperty({ item_id: newItemId, name, value });
            }
          }
        }
      }
      setOpenDialog(false);
      fetchItems();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to save item';
      setError(errorMessage);
    }
  };

  const addPropertyRow = () => {
    setProperties(prev => [...prev, { name: '', value: '' }]);
  };

  const updatePropertyField = (index: number, field: 'name' | 'value', value: string) => {
    setProperties(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
  };

  const removePropertyRow = (index: number) => {
    setProperties(prev => prev.filter((_, i) => i !== index));
  };

  // Tasting notes management functions
  const handleAddTastingNote = async () => {
    if (!newNoteText.trim() || !editingItem?.id) return;
    
    try {
      await createTastingNote({ item_id: editingItem.id, notes: newNoteText.trim() });
      setNewNoteText('');
      // Reload tasting notes
      const notesRes = await listTastingNotes(editingItem.id);
      const notes = notesRes.data.data || [];
      setTastingNotes(notes);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to add tasting note';
      setError(errorMessage);
    }
  };

  const handleEditTastingNote = (note: TastingNote) => {
    setEditingNoteId(note.id!);
    setEditingNoteText(note.notes);
    setEditingNoteSource(note.source || '');
    setEditingNoteFname(note.fname || '');
    setEditingNoteLname(note.lname || '');
    setEditingNoteEmail(note.email || '');
  };

  const handleSaveTastingNote = async () => {
    if (!editingNoteText.trim() || !editingNoteId || !editingItem?.id) return;
    
    try {
      const updateData: Partial<TastingNote> = {
        notes: editingNoteText.trim(),
        source: editingNoteSource.trim() || undefined,
        fname: editingNoteFname.trim() || undefined,
        lname: editingNoteLname.trim() || undefined,
        email: editingNoteEmail.trim() || undefined,
      };
      
      await updateTastingNote(editingNoteId, updateData);
      setEditingNoteId(null);
      setEditingNoteText('');
      setEditingNoteSource('');
      setEditingNoteFname('');
      setEditingNoteLname('');
      setEditingNoteEmail('');
      // Reload tasting notes
      const notesRes = await listTastingNotes(editingItem.id);
      const notes = notesRes.data.data || [];
      setTastingNotes(notes);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update tasting note';
      setError(errorMessage);
    }
  };

  const handleDeleteTastingNote = async (noteId: number) => {
    if (!window.confirm('Are you sure you want to delete this tasting note?')) return;
    
    try {
      await deleteTastingNote(noteId);
      // Reload tasting notes
      if (editingItem?.id) {
        const notesRes = await listTastingNotes(editingItem.id);
        const notes = notesRes.data.data || [];
        setTastingNotes(notes);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to delete tasting note';
      setError(errorMessage);
    }
  };

  const handleCancelEditTastingNote = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
    setEditingNoteSource('');
    setEditingNoteFname('');
    setEditingNoteLname('');
    setEditingNoteEmail('');
  };

  const parseCSV = (csvText: string): Array<{ name: string; cost: number }> => {
    const lines = csvText.trim().split('\n');
    const headers = lines[0].toLowerCase().split(',').map(h => h.trim().replace(/"/g, ''));
    
    // Validate headers
    const nameIndex = headers.findIndex(h => h === 'name');
    const costIndex = headers.findIndex(h => h === 'cost');
    
    if (nameIndex === -1 || costIndex === -1) {
      throw new Error('CSV must contain "NAME" and "COST" columns');
    }

    const results: Array<{ name: string; cost: number }> = [];
    
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      // Simple CSV parsing that handles quoted values
      const values: string[] = [];
      let current = '';
      let inQuotes = false;
      
      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim()); // Add the last value
      
      const name = values[nameIndex]?.replace(/"/g, '').trim();
      const costStr = values[costIndex]?.replace(/"/g, '').trim();
      
      if (!name) continue;
      
      const cost = parseFloat(costStr);
      if (isNaN(cost) || cost < 0) {
        throw new Error(`Invalid cost value "${costStr}" for item "${name}"`);
      }
      
      results.push({ name, cost });
    }
    
    return results;
  };

  const generateItemCode = (name: string): string => {
    // Generate a code based on the name
    const prefix = name.substring(0, 3).toUpperCase();
    const timestamp = Date.now().toString().slice(-6);
    return `${prefix}-${timestamp}`;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadResults(null);
    setError('');

    try {
      const text = await file.text();
      const csvData = parseCSV(text);
      
      if (csvData.length === 0) {
        setError('No valid data found in CSV file');
        return;
      }

      let created = 0;
      let updated = 0;
      let inventoryUpdated = 0;
      const errors: string[] = [];

      for (let i = 0; i < csvData.length; i++) {
        const { name, cost } = csvData[i];
        
        // Skip if name is empty or cost is invalid
        if (!name || cost === undefined || cost === null || isNaN(cost) || cost < 0) {
          errors.push(`Skipping "${name}": Invalid name or cost value`);
          continue;
        }
        
        try {
          // Check if item exists by name
          const existingItem = items.find(item => 
            item.name.toLowerCase() === name.toLowerCase()
          );

          let itemId: number;

          if (existingItem) {
            // Update existing item's cost - only send defined values
            const updateData: Partial<Item> = {};
            if (cost !== undefined && cost !== null) {
              updateData.cost = cost;
            }
            await updateItem(existingItem.id!, updateData);
            itemId = existingItem.id!;
            updated++;
          } else {
            // Create new item
            const newItem: Partial<Item> = {
              code: generateItemCode(name),
              name,
              description: `Imported from CSV: ${name}`,
              cost,
              price: cost * 1.5, // Set price as 50% markup on cost
            };
            
            const createdItem = await createItem(newItem as Item);
            itemId = createdItem.data.data.id;
            created++;
          }

          // Increase on_hand directly on ITEM
          try {
            const matched = items.find(i => i.id === itemId);
            const currentOnHand = matched?.on_hand ?? 0;
            await updateItem(itemId, { on_hand: currentOnHand + 1 });
            inventoryUpdated++;
          } catch (invErr: any) {
            const errorMsg = `Failed to update on_hand for "${name}": ${invErr.response?.data?.message || invErr.message}`;
            errors.push(errorMsg);
          }

          setUploadProgress(((i + 1) / csvData.length) * 100);
        } catch (err: any) {
          const errorMsg = `Failed to process "${name}": ${err.response?.data?.message || err.message}`;
          errors.push(errorMsg);
        }
      }

      setUploadResults({ created, updated, inventoryUpdated, errors });
      
      // Refresh items list
      await fetchItems();
      
    } catch (err: any) {
      setError(err.message || 'Failed to process CSV file');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Inline editing state
  const [editingCell, setEditingCell] = useState<{ id: string | number; field: string } | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');

  // Handle cell edit start
  const handleCellEditStart = (params: any) => {
    if (!user?.is_admin) return;
    setEditingCell({ id: params.id, field: params.field });
    setEditingValue(params.value || '');
  };

  // Handle cell edit stop
  const handleCellEditStop = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  // Handle cell edit commit
  const handleCellEditCommit = async (params: any) => {
    if (!user?.is_admin) return;
    
    try {
      const { id, field } = params;
      const newValue = editingValue;
      const updatedItem = { ...items.find(item => item.id === id), [field]: newValue };
      await updateItem(id, updatedItem);
      
      // Update local state
      setItems(prev => prev.map(item => 
        item.id === id ? { ...item, [field]: newValue } : item
      ));
      
      setEditingCell(null);
      setEditingValue('');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update item';
      setError(errorMessage);
      console.error('Inline edit error:', err);
      
      // Revert the change in the grid by refreshing the data
      fetchItems();
      setEditingCell(null);
      setEditingValue('');
    }
  };

  // Render editable cell
  const renderEditableCell = (params: any, field: string) => {
    if (editingCell?.id === params.id && editingCell?.field === field) {
      return (
        <TextField
          value={editingValue}
          onChange={(e) => setEditingValue(e.target.value)}
          onBlur={() => handleCellEditCommit({ ...params, value: editingValue })}
                      onKeyDown={(e) => {
              if (e.key === ' ') {
                // Allow spacebar to be typed normally
                e.stopPropagation();
              } else if (e.key === 'Enter') {
                e.preventDefault();
                handleCellEditCommit({ ...params, value: editingValue });
              } else if (e.key === 'Escape') {
                e.preventDefault();
                handleCellEditStop();
              }
            }}
          size="small"
          autoFocus
          fullWidth
          sx={{ 
            '& .MuiInputBase-root': { 
              height: '100%',
              '& input': { 
                padding: '8px 12px',
                height: '100%',
                boxSizing: 'border-box'
              }
            }
          }}
        />
      );
    }
    return (
      <Box
        onClick={() => user?.is_admin && handleCellEditStart(params)}
        sx={{ 
          cursor: user?.is_admin ? 'pointer' : 'default',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          boxSizing: 'border-box',
          '&:hover': user?.is_admin ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {}
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {params.value || ''}
        </span>
        {user?.is_admin && (
          <EditOutlinedIcon 
            sx={{ 
              fontSize: '16px', 
              color: 'action.disabled',
              ml: 1,
              opacity: 0.6
            }} 
          />
        )}
      </Box>
    );
  };

  const columns: GridColDef[] = isMobile ? [
    { 
      field: 'display_name', 
      headerName: 'Name', 
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        return (
          <Box
            onClick={() => handleEditItem(params.row)}
            sx={{ 
              cursor: 'pointer',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              padding: '8px 12px',
              boxSizing: 'border-box',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
            }}
          >
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {params.value || params.row.name || ''}
            </span>
          </Box>
        );
      }
    },
    {
      field: 'on_hand',
      headerName: 'Stock',
      width: 100,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Tooltip title="Decrease quantity">
              <IconButton
                size="small"
                onClick={() => handleInventoryUpdate(params.row, -1)}
                disabled={params.value <= 0}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ minWidth: '25px', textAlign: 'center', fontSize: '0.875rem' }}>
              {params.value || 0}
            </Typography>
            <Tooltip title="Increase quantity">
              <IconButton
                size="small"
                onClick={() => handleInventoryUpdate(params.row, 1)}
              >
                <AddIcon2 fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 80,
      getActions: (params) => {
        if (!params || !params.row) return [];
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            onClick={() => handleEditItem(params.row)}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleDeleteItem(params.row.code)}
          />,
        ];
      },
    },
  ] : [
    { field: 'code', headerName: 'Code', width: 150 },
    { 
      field: 'name', 
      headerName: 'Name', 
      width: 200,
      renderCell: (params) => renderEditableCell(params, 'name')
    },
    { 
      field: 'display_name', 
      headerName: 'Display Name', 
      width: 200,
      renderCell: (params) => renderEditableCell(params, 'display_name')
    },
    { field: 'description', headerName: 'Description', width: 300 },
    { 
      field: 'cost', 
      headerName: 'Cost (€)', 
      width: 120,
      valueFormatter: (value: any) => {
        if (value == null || value === undefined) return '€0.00';
        return `€${Number(value).toFixed(2)}`;
      }
    },
    { 
      field: 'price', 
      headerName: 'Price (€)', 
      width: 120,
      valueFormatter: (value: any) => {
        if (value == null || value === undefined) return '€0.00';
        return `€${Number(value).toFixed(2)}`;
      }
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: 220,
      renderCell: (params) => renderEditableCell(params, 'tags')
    },
    {
      field: 'is_alcohol',
      headerName: 'Alcohol',
      width: 100,
      type: 'boolean',
      renderCell: (params) => (
        <Switch
          checked={Boolean(params.value)}
          onChange={() => handleAlcoholToggle(params.row)}
          size="small"
          color="primary"
        />
      ),
    },
    {
      field: 'on_hand',
      headerName: 'On Hand',
      width: 150,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Decrease quantity">
            <IconButton
              size="small"
              onClick={() => handleInventoryUpdate(params.row, -1)}
              disabled={params.value <= 0}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="body2" sx={{ minWidth: '30px', textAlign: 'center' }}>
            {params.value || 0}
          </Typography>
          <Tooltip title="Increase quantity">
            <IconButton
              size="small"
              onClick={() => handleInventoryUpdate(params.row, 1)}
            >
              <AddIcon2 fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params) => {
        if (!params || !params.row) return [];
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            onClick={() => handleEditItem(params.row)}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleDeleteItem(params.row.code)}
          />,
        ];
      },
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" className="admin-mobile-only" sx={{ mb: 2 }}>
          Items Management
        </Typography>
        <Typography variant="h4" className="admin-desktop-only">
          Items Management
        </Typography>
        <Box 
          display="flex" 
          className="admin-mobile-header admin-desktop-header"
        >
          <TextField
            placeholder="Search code, name, description, cost, price"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="admin-mobile-search admin-desktop-search"
          />
          <Box display="flex" gap={2} alignItems="center" className="admin-mobile-buttons">
            {user?.is_admin && (
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={handleUploadClick}
                disabled={uploading}
                className="admin-mobile-button"
              >
                <span className="admin-mobile-only">Import</span>
                <span className="admin-desktop-only">Import CSV</span>
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
              className="admin-mobile-button"
            >
              Add Item
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept=".csv"
        style={{ display: 'none' }}
      />

      {/* Upload progress */}
      {uploading && (
        <Box mb={2}>
          <Typography variant="body2" mb={1}>
            Processing CSV file... {Math.round(uploadProgress)}%
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}

      {/* Upload results */}
      {uploadResults && (
        <Box mb={2}>
          <Typography variant="h6" mb={1}>Upload Results:</Typography>
          <Box display="flex" gap={1} mb={1}>
            <Chip 
              label={`${uploadResults.created} created`} 
              color="success" 
              size="small" 
            />
            <Chip 
              label={`${uploadResults.updated} updated`} 
              color="info" 
              size="small" 
            />
            <Chip 
              label={`${uploadResults.inventoryUpdated} inventory updated`} 
              color="warning" 
              size="small" 
            />
            {uploadResults.errors.length > 0 && (
              <Chip 
                label={`${uploadResults.errors.length} errors`} 
                color="error" 
                size="small" 
              />
            )}
          </Box>
          {uploadResults.errors.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {uploadResults.errors.slice(0, 3).join(', ')}
                {uploadResults.errors.length > 3 && ` and ${uploadResults.errors.length - 3} more errors`}
              </Typography>
            </Alert>
          )}
        </Box>
      )}

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setError('')}
          action={
            <Button color="inherit" size="small" onClick={() => setError('')}>
              Dismiss
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Box 
        className="admin-mobile-table admin-desktop-table"
        sx={{ 
          width: '100%',
          overflow: 'hidden'
        }}
      >
        <DataGrid
          rows={items}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id || row.code}
          pageSizeOptions={[10, 20, 50, 100, 500]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 50 },
            },
          }}
          columnVisibilityModel={isMobile ? {
            code: false,
            name: false,
            display_name: true,
            description: false,
            cost: false,
            price: false,
            tags: false,
            is_alcohol: false,
            on_hand: true,
            actions: true
          } : {
            code: true,
            name: true,
            display_name: true,
            description: true,
            cost: true,
            price: true,
            tags: true,
            is_alcohol: true,
            on_hand: true,
            actions: true
          }}
          sx={{
            '& .MuiDataGrid-main': {
              overflow: 'hidden'
            }
          }}
        />
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Item' : 'Add New Item'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField
              label="Code"
              value={formData.code || ''}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Name"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Display Name (optional)"
              value={formData.display_name || ''}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              fullWidth
              helperText="Custom name shown to customers. Leave blank to use the main name."
            />
            <TextField
              label="Cost (€)"
              type="number"
              value={formData.cost || ''}
              onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) || 0 })}
              fullWidth
              required
              inputProps={{ step: 0.01, min: 0 }}
            />
            <TextField
              label="Price (€)"
              type="number"
              value={formData.price || ''}
              onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
              fullWidth
              required
              inputProps={{ step: 0.01, min: 0 }}
            />
            <TextField
              label="Tags (comma-separated)"
              value={formData.tags || ''}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              fullWidth
            />
            <Box sx={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="h6">Barcode</Typography>
              {formData.barcode && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, bgcolor: 'white', borderRadius: 1 }}>
                  <Barcode 
                    value={formData.barcode} 
                    width={2}
                    height={50}
                    fontSize={12}
                    margin={10}
                  />
                </Box>
              )}
              <TextField
                label="Barcode Value"
                value={formData.barcode || ''}
                onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                fullWidth
                helperText="Enter the barcode number to display the barcode above"
              />
            </Box>
            <TextField
              label="On Hand"
              type="number"
              value={formData.on_hand ?? ''}
              onChange={(e) => {
                const val = e.target.value === '' ? undefined : Math.max(0, Math.floor(Number(e.target.value)));
                setFormData({ ...formData, on_hand: val as number | undefined });
              }}
              fullWidth
              required
              inputProps={{ step: 1, min: 0 }}
            />
            <TextField
              label="Description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              required
              multiline
              rows={4}
              sx={{ gridColumn: 'span 2' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(formData.is_alcohol)}
                  onChange={(e) => setFormData({ ...formData, is_alcohol: e.target.checked })}
                  color="primary"
                />
              }
              label="Is Alcoholic Beverage"
              sx={{ gridColumn: 'span 2' }}
            />
          </Box>

          {/* Properties Section */}
          <Accordion sx={{ mt: 2 }} expanded={propertiesExpanded} onChange={(_, exp) => setPropertiesExpanded(exp)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Properties</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {properties.map((p, idx) => (
                  <Box key={p.id ?? idx} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 1, alignItems: 'center' }}>
                    <TextField
                      label="Name"
                      size="small"
                      value={p.name || ''}
                      onChange={(e) => updatePropertyField(idx, 'name', e.target.value)}
                    />
                    <TextField
                      label="Value"
                      size="small"
                      value={p.value || ''}
                      onChange={(e) => updatePropertyField(idx, 'value', e.target.value)}
                    />
                    <IconButton aria-label="delete-property" onClick={() => removePropertyRow(idx)}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button startIcon={<AddCircleOutlineIcon />} onClick={addPropertyRow} variant="text">Add Property</Button>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Tasting Notes Section */}
          {editingItem && (
            <Accordion sx={{ mt: 2 }} expanded={tastingNotesExpanded} onChange={(_, exp) => setTastingNotesExpanded(exp)}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Tasting Notes ({tastingNotes.length})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Add new tasting note */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      label="Add new tasting note"
                      multiline
                      rows={3}
                      value={newNoteText}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      fullWidth
                      placeholder="Enter your tasting notes here..."
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddTastingNote}
                      disabled={!newNoteText.trim()}
                      sx={{ alignSelf: 'flex-start', minWidth: '100px' }}
                    >
                      Add Note
                    </Button>
                  </Box>

                  {/* Existing tasting notes */}
                  {tastingNotes.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {tastingNotes.map((note) => (
                        <Box key={note.id} sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, p: 2 }}>
                          {editingNoteId === note.id ? (
                            // Edit mode
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <TextField
                                label="Tasting Notes"
                                multiline
                                rows={3}
                                value={editingNoteText}
                                onChange={(e) => setEditingNoteText(e.target.value)}
                                fullWidth
                                required
                              />
                              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                <TextField
                                  label="Source (optional)"
                                  value={editingNoteSource}
                                  onChange={(e) => setEditingNoteSource(e.target.value)}
                                  fullWidth
                                  placeholder="e.g., Wine Spectator, Robert Parker"
                                />
                                <TextField
                                  label="Email (optional)"
                                  type="email"
                                  value={editingNoteEmail}
                                  onChange={(e) => setEditingNoteEmail(e.target.value)}
                                  fullWidth
                                  placeholder="user@example.com"
                                />
                              </Box>
                              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                <TextField
                                  label="First Name (optional)"
                                  value={editingNoteFname}
                                  onChange={(e) => setEditingNoteFname(e.target.value)}
                                  fullWidth
                                  placeholder="John"
                                />
                                <TextField
                                  label="Last Name (optional)"
                                  value={editingNoteLname}
                                  onChange={(e) => setEditingNoteLname(e.target.value)}
                                  fullWidth
                                  placeholder="Doe"
                                />
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button
                                  size="small"
                                  onClick={handleCancelEditTastingNote}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={handleSaveTastingNote}
                                  disabled={!editingNoteText.trim()}
                                >
                                  Save
                                </Button>
                              </Box>
                            </Box>
                          ) : (
                            // View mode
                            <Box>
                              <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                                {note.notes}
                              </Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                  Added: {note.created_at ? new Date(note.created_at).toLocaleDateString() : 'Unknown date'}
                                  {note.source ? ` • Source: ${note.source}` : ''}
                                  {note.fname && note.lname ? ` • By: ${note.fname} ${note.lname}` : ''}
                                  {note.email && !note.fname && !note.lname ? ` • By: ${note.email}` : ''}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    size="small"
                                    startIcon={<EditOutlinedIcon />}
                                    onClick={() => handleEditTastingNote(note)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteOutlineIcon />}
                                    onClick={() => note.id && handleDeleteTastingNote(note.id)}
                                  >
                                    Delete
                                  </Button>
                                </Box>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No tasting notes yet. Add one above to get started.
                    </Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Images Section */}
          {editingItem && currentItemImages.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>Item Images</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {currentItemImages.map((image, index) => (
                  <Box key={image.id} sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, p: 1 }}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 1, textTransform: 'capitalize' }}>
                      {image.image_type} Image
                    </Typography>
                    <img
                      src={`data:image/jpeg;base64,${image.image_data}`}
                      alt={`${image.image_type} view`}
                      style={{ 
                        width: '150px', 
                        height: '150px', 
                        objectFit: 'cover',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setPreviewSrc(`data:image/jpeg;base64,${image.image_data}`);
                        setPreviewOpen(true);
                      }}
                    />
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                      Added: {new Date(image.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {editingItem && user?.is_admin && (
            <>
              <Button
                color="secondary"
                onClick={async () => {
                  try {
                    await makeItemLabel(editingItem.id!, 'detect');
                    const imagesRes = await getItemImages(editingItem.id!);
                    setCurrentItemImages(imagesRes.data.data || []); // refresh in place
                  } catch (err: any) {
                    alert(err.response?.data?.message || 'Failed to extract label');
                  }
                }}
              >
                Extract Label
              </Button>
              <Button
                color="secondary"
                onClick={async () => {
                  try {
                    await makeItemLabel(editingItem.id!, 'generate');
                    const imagesRes = await getItemImages(editingItem.id!);
                    setCurrentItemImages(imagesRes.data.data || []); // refresh in place
                  } catch (err: any) {
                    alert(err.response?.data?.message || 'Failed to generate label');
                  }
                }}
              >
                Generate Label
              </Button>
            </>
          )}
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm">
        <DialogContent>
          {previewSrc && (
            <img src={previewSrc} alt="preview" style={{ width: '100%', height: 'auto' }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Items;
